/**
 * @file 端能力出口
 * @author weijiaxun <weijiaxun@baidu.com>
 */
import { NOT_EXIST, NATIVE_NOT_EXIST } from '../constants/status';
const matrix = {};
let invokerReference = null;
let activeAppName = '';
/**
 * 注册指定 app 的端能力实现
 *
 * @param {string} app app 名称
 * @param {MatrixNativeAppLoader} appInvoker 对应的端能力实现
 */
export function nativeRegister(app, appInvoker) {
    // 禁止重复注册
    matrix[app] = matrix[app] || appInvoker;
}
/**
 * 初始化矩阵端能力配置
 *
 * @param {MatrixNativeConfig} config 矩阵端能力配置项
 * @param {Function} callback 加载成功回调
 * @return {MatrixNativeInvoker} 对外的矩阵能力调用对象
 */
function init(config, callback) {
    // 原始apiList列表
    let srcKeys = (invokerReference && Object.keys(invokerReference)) || [];
    // 再次初始化apiList列表
    let destKeys = config.jsApiList;
    // 获取以上两者的diff
    let diffApiList = destKeys.filter(dkey => { return srcKeys.every(sKey => { return sKey != dkey; }); });
    // 单例 invoker，不允许重复初始化
    if (invokerReference !== null && diffApiList.length === 0) {
        return invokerReference;
    }
    const appName = getActiveAppName();
    // 根据 appName 以及对应的端能力实现加载器的 load 方法，创建加载对应端能力 js sdk 的 request
    const appLoader = matrix[appName];
    if (!appLoader) {
        console.error('端SDK未注册，请优先import!');
    }
    const request = appLoader
        ? new Promise(loadAppNative) : undefined;
    // 获取传入配置中，用户希望调用的 api list
    // const jsApiList = config.jsApiList;
    const invoker = diffApiList.reduce((methodInvoker, methodName) => {
        // 用空的调用函数填满对外调用的对象
        methodInvoker[methodName] = createDefaultInvokeMethod(methodName, request);
        return methodInvoker;
    }, {});
    invokerReference = invoker;
    return invoker;
    // 加载 js sdk 的 Promise
    function loadAppNative(resolve) {
        appLoader.load(function (appNative) {
            // 加载完成
            diffApiList.forEach(method => {
                // 将初始化阶段空的调用函数，重写为对外调用器的端能力方法
                invoker[method] = function (nativeConfig) {
                    if (!appNative[method]) {
                        return Promise.reject(NOT_EXIST);
                    }
                    return appNative[method].call(appNative, mergeArgsByAppName(nativeConfig));
                };
            });
            // 调用加载成功回调
            callback && callback(invoker);
            resolve(invoker);
        });
    }
}
/**
 * 获取当前运行环境所处的 appName
 *
 * @return {string} appName
 */
function getActiveAppName() {
    if (activeAppName === '') {
        // 根据 is 方法，计算出当前运行时对应的 appName
        const activeAppList = Object.keys(matrix).filter(app => matrix[app].is());
        activeAppName = activeAppList.length > 0 ? activeAppList[0] : '';
    }
    return activeAppName;
}
/**
 * 创建默认调用方法的高阶函数
 * 仅在请求调用的端能力不存在（未注册），或是 js sdk 未加载完成时，才会调用其返回的方法
 * 用于在 js sdk 加载之前就调用了端能力时，延迟调用端能力
 *
 * @param {string} methodName 调用方法名
 * @param {Promise<MatrixNativeInvoker>} request 用于异步请求 js sdk 的请求对象
 * @return {Promise<any>} 调用结果的 Promise 对象
 */
function createDefaultInvokeMethod(methodName, request) {
    return function (nativeConfig) {
        // 如果 request 为空，说明未注册对应端的 sdk
        if (!request) {
            return Promise.reject(NATIVE_NOT_EXIST);
        }
        return request.then(invoker => {
            if (!invoker[methodName]) {
                return Promise.reject(NOT_EXIST);
            }
            return invoker[methodName].call(invoker, mergeArgsByAppName(nativeConfig));
        });
    };
}
/**
 * 根据 appName，生成实际分发到 app 端能力的参数
 * `_${appName}` 被视为私有属性
 *
 * @param nativeConfig 合并前的配置项
 * @return 合并后的配置项
 */
function mergeArgsByAppName(nativeConfig = {}) {
    const appName = '_' + getActiveAppName();
    // 获取 _ 开头的私有 config
    const privateNativeConfig = nativeConfig[appName];
    // 清洗掉 _ 开头的私有 config
    const rinsedConfig = Object.keys(nativeConfig)
        .filter(key => key[0] !== '_')
        .reduce((rinsedConfig, nextKey) => {
        rinsedConfig[nextKey] = nativeConfig[nextKey];
        return rinsedConfig;
    }, {});
    return {
        ...rinsedConfig,
        [appName]: privateNativeConfig
    };
}
export default {
    init,
    getActiveAppName
};
//# sourceMappingURL=index.js.map